import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ExchangeOffice, Currency } from '../../types';

interface ExchangeOfficeState {
  offices: ExchangeOffice[];
  currentOffice: ExchangeOffice | null;
  loading: boolean;
}

const initialState: ExchangeOfficeState = {
  offices: [],
  currentOffice: null,
  loading: false,
};

const exchangeOfficeSlice = createSlice({
  name: 'exchangeOffice',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setOffices: (state, action: PayloadAction<ExchangeOffice[]>) => {
      state.offices = action.payload;
    },
    addOffice: (state, action: PayloadAction<ExchangeOffice>) => {
      state.offices.push(action.payload);
    },
    updateOffice: (state, action: PayloadAction<ExchangeOffice>) => {
      const index = state.offices.findIndex((o) => o.id === action.payload.id);
      if (index !== -1) {
        state.offices[index] = action.payload;
      }
      if (state.currentOffice?.id === action.payload.id) {
        state.currentOffice = action.payload;
      }
    },
    deleteOffice: (state, action: PayloadAction<number>) => {
      state.offices = state.offices.filter((o) => o.id !== action.payload);
      if (state.currentOffice?.id === action.payload) {
        state.currentOffice = null;
      }
    },
    toggleOfficeStatus: (state, action: PayloadAction<number>) => {
      const office = state.offices.find((o) => o.id === action.payload);
      if (office) {
        office.status = office.status === 'active' ? 'inactive' : 'active';
      }
      if (state.currentOffice?.id === action.payload) {
        state.currentOffice.status = state.currentOffice.status === 'active' ? 'inactive' : 'active';
      }
    },
    setCurrentOffice: (state, action: PayloadAction<ExchangeOffice | null>) => {
      state.currentOffice = action.payload;
    },
    updateCurrencies: (state, action: PayloadAction<{ officeId: number; currencies: Currency[] }>) => {
      const office = state.offices.find((o) => o.id === action.payload.officeId);
      if (office) {
        office.currencies = action.payload.currencies;
      }
      if (state.currentOffice?.id === action.payload.officeId) {
        state.currentOffice.currencies = action.payload.currencies;
      }
    },
    updateOfficeSettings: (state, action: PayloadAction<{ officeId: number; logo?: string; video?: string; videoOption?: string }>) => {
      const office = state.offices.find((o) => o.id === action.payload.officeId);
      if (office) {
        if (action.payload.logo !== undefined) office.logo = action.payload.logo;
        if (action.payload.video !== undefined) office.video = action.payload.video;
        if (action.payload.videoOption !== undefined) office.videoOption = action.payload.videoOption;
      }
      if (state.currentOffice?.id === action.payload.officeId) {
        if (action.payload.logo !== undefined) state.currentOffice.logo = action.payload.logo;
        if (action.payload.video !== undefined) state.currentOffice.video = action.payload.video;
        if (action.payload.videoOption !== undefined) state.currentOffice.videoOption = action.payload.videoOption;
      }
    },
  },
});

export const {
  setLoading,
  setOffices,
  addOffice,
  updateOffice,
  deleteOffice,
  toggleOfficeStatus,
  setCurrentOffice,
  updateCurrencies,
  updateOfficeSettings,
} = exchangeOfficeSlice.actions;
export default exchangeOfficeSlice.reducer;
